import { flattenDeep, last, join, first, groupBy, forEach } from 'lodash';
import { calculateTime, wait } from './functions';

export const functionsMixin = {
  // ***************************************************************************************
  // getWords() permet de récupérer les mots de tous les lignes du sous-titre courant
  // ***************************************************************************************
  getWords() {
    return flattenDeep(this.subtitle.lines.map((x) => x.words));
  },

  // ***************************************************************************************
  // getWordsOfSubtitle() permet de récupérer les mots de tous les lignes du sous-titre
  // ***************************************************************************************
  getWordsOfSubtitle(subtitle) {
    return flattenDeep(subtitle.lines.map((x) => x.words));
  },

  // ***************************************************************************************
  // getNextWord() permet de récupérer le prochain mot
  // ***************************************************************************************
  getNextWord(checkNextLine = true) {
    const nextWord = this.currentLine.words[this.currentWordIndex + 1];
    if (nextWord) {
      return nextWord;
    }

    if (checkNextLine) {
      const nextLine = this.subtitle.lines[this.currentLineIndex + 1];
      if (nextLine && nextLine.words.length) {
        return first(nextLine.words);
      }
    }

    return null;
  },

  // ***************************************************************************************
  // getPrevWord() permet de récupérer le prochain mot
  // ***************************************************************************************
  getPrevWord(checkPrevLine = true) {
    const prevWord = this.currentLine.words[this.currentWordIndex - 1];
    if (prevWord) {
      return prevWord;
    }

    if (checkPrevLine) {
      const prevLine = this.subtitle.lines[this.currentLineIndex - 1];
      if (prevLine && prevLine.words.length) {
        return last(prevLine.words);
      }
    }

    return null;
  },

  // ***************************************************************************************
  // getNextWordsOfLine() permet de récupérer les prochains mots de la ligne
  // ***************************************************************************************
  getNextWordsOfLine(index) {
    return this.currentLine.words.filter((x) => x.index >= index);
  },

  // ***************************************************************************************
  // setWordsToNewLine() permet de mettre les mots à la ligne suivante ou nouvelle ligne
  // ***************************************************************************************
  setWordsToNewLine(index, nextWords) {
    if (!nextWords.length) {
      return;
    }
    const currentLine = this.subtitle.lines[this.currentLineIndex];
    const nextLine = this.subtitle.lines[this.currentLineIndex + 1];

    if (nextLine) {
      const endSentence = nextWords.map((x) => x.text).join(' ');
      const nextSentence = this.getSentence(nextLine);
      const maxLength = this.getMaxLength(nextLine);

      if (nextSentence.length + endSentence.length > maxLength) {
        this.cancelUndo();
        return;
      }

      currentLine.words.splice(index, nextWords.length);
      nextLine.words.unshift(...nextWords);
    } else {
      const newLine = this.createNewLine(this.subtitle, currentLine);
      if (!newLine) {
        this.cancelUndo();
        return;
      }

      currentLine.words.splice(index, nextWords.length);
      newLine.words.push(...nextWords);
      this.subtitle.lines.push(newLine);
    }
  },

  // ***************************************************************************************
  // getCurrentSentence() permet de récupérer la phrase de la ligne courante
  // ***************************************************************************************
  getCurrentSentence() {
    return this.currentLine.words.map((x) => x.text).join(' ');
  },

  // ***************************************************************************************
  // hasSomeWordsColored() permet de vérifier s'il y a des mots colorées
  // ***************************************************************************************
  hasSomeWordsColored(line) {
    return line.words.some((x) => x.color !== '#FFFFFF');
  },

  // ***************************************************************************************
  // getMaxLength()  si la phrase est colorée, alors on doit utiliser maxLengthColor plutôt que maxLength
  // ***************************************************************************************
  getMaxLength(line) {
    const { maxLength, maxLengthColor } = this.props.subtitleParameters;

    return this.hasSomeWordsColored(line) ? maxLengthColor : maxLength;
  },

  // ***************************************************************************************
  // getNbChars() permet de récupérer le nombre de caractères total
  // ***************************************************************************************
  getNbChars() {
    return this.getWords()
      .map((x) => x.text)
      .join(' ').length;
  },

  // ***************************************************************************************
  // getNbCharsOfSubtitle() permet de récupérer le nombre de caractères total du sous-titre
  // ***************************************************************************************
  getNbCharsOfSubtitle(subtitle) {
    return this.getWordsOfSubtitle(subtitle)
      .map((x) => x.text)
      .join(' ').length;
  },

  // ***************************************************************************************
  // getSentence() permet de récupérer la phrase de la ligne
  // ***************************************************************************************
  getSentence(line) {
    return line.words.map((x) => x.text).join(' ');
  },

  getCompleteSentence(lines) {
    let sentence = '';

    lines.forEach((line) => {
      sentence += this.getSentence(line);
    });

    return sentence;
  },

  // ***************************************************************************************
  // isLastLetterOfLine() permet de dire si la lettre est la dernière de la ligne
  // ***************************************************************************************
  isLastLetterOfLine() {
    if (!this.currentLine.words.length) {
      return true;
    }

    const lastWord = last(this.currentLine.words);

    if (!lastWord.letters.length) {
      return true;
    }

    const lastIndex = last(lastWord.letters).indexLetter;
    return this.currentLetterIndex === lastIndex;
  },

  // ***************************************************************************************
  // getTeletextMinMax() permet de récupérer le x min et x max
  // ***************************************************************************************
  getTeletextMinMax(line)
  {
    var len = 0;
    var x = 2; // 2 x Start Boxing
    
    if ("words" in line)
    {
      
      line.words.forEach ((word,index) =>{
        if (!index) 
        {
          if ("color" in word) 
            if (word.color.toLowerCase() !== "#ffffff") x++;
        }
        else
          len++;
        len += word.text.length;
      })
    }
    if (!("doubleHeight" in line )) line.doubleHeight = true; // pas normal
    if (line.doubleHeight) x++;

    return {min:x, max: 39 - len, len: len, dbheight: line.doubleHeight}
  },




  // ***************************************************************************************
  // getCoordinateX() permet de récupérer la coordonnée X
  // ***************************************************************************************
  getCoordinateX(line, teletext = false) {
    // 0 = left , 1 = right , 2 = center, 3 = position dans "hPosition" en %

    const align = line.align ?? this.props.subtitleParameters.defaultAlignment;


    if (teletext)
    {

      const minmax = this.getTeletextMinMax(line);
      switch (align)
      {
        case 0:
          return (this.width * minmax.min) / 40;
        case 1:
          return (this.width * minmax.max) / 40;
        case 2:
          var ret = (20 - minmax.len/2) ;
          if (ret < minmax.min) ret = minmax.min;
          return (ret * this.width) / 40;
        case 3:
          if (!("horizontalPos" in line)) line.horizontalPos = 0;
          var min = Math.floor(line.horizontalPos * 40 / 100);
          if (min < minmax.min) min = minmax.min;
          return (min * this.width) / 40;


      }


      var ret = 2; // 2 * Boxing
      if (line.doubleHeight) ret++;
      if (("words" in line) && line.words.length)
        if (line.words[0].color.toLowerCase() !== "#ffffff") ret++;
      return (this.width * ret) / 40;

    }








    switch (align) {
      // Aligner à gauche
      case 0:
        return this.metricX;

      // Aligner à droite
      case 1:
        return this.width - line.width - this.metricX;

      // Centrer
      case 2:
        return (this.width - line.width) / 2;

      // Manual
      case 3:
        if (!"horizontalPos" in line)
          return (this.width - line.width) / 2;          

        if (teletext)
        {
            var halign = Math.floor(line.horizontalPos * 40 / 100);
            return (this.width * halign) / 40;
        }
        return (parseFloat(line.horizontalPos) / 100) * this.width;

        

      default:
        return 0;
    }
  },

  // ***************************************************************************************
  // setAlignToLines() permet de mettre le type d'alignement aux lignes
  // ***************************************************************************************
  setAlignToLines(align) {

    if (!this.currentLine) {
      this.subtitle.lines.forEach((line) => {
        line.align = align;
        line.horizontalPos = null;
      });

      return;
    }

    if (this.currentLine.align === align) {
      this.subtitle.lines.forEach((line) => {
        line.align = align;
        line.horizontalPos = null;
      });
    } else {
      this.currentLine.align = align;
    }
  },

  // ***************************************************************************************
  // deleteLine() permet de supprimer la ligne
  // ***************************************************************************************
  deleteLine() {
    // on met à jour le timecode
    let inTime = this.currentLine.in;
    let outTime = this.currentLine.out;

    if (this.currentLineIndex === 0) {
      const nextLine = this.subtitle.lines[this.currentLineIndex + 1];
      if (nextLine) {
        nextLine.in = inTime;
      }
    } else {
      // dernière ligne
      if (this.currentLineIndex === this.subtitle.lines.length - 1) {
        const prevLine = this.subtitle.lines[this.currentLineIndex - 1];
        if (prevLine) {
          prevLine.out = outTime;
        }
      } else {
        // entre les deux
        const prevLine = this.subtitle.lines[this.currentLineIndex - 1];
        if (prevLine) {
          prevLine.out = inTime;
        }
        const nextLine = this.subtitle.lines[this.currentLineIndex + 1];
        if (nextLine) {
          prevLine.in = outTime;
        }
      }
    }

    // on supprime la ligne
    this.subtitle.lines.splice(this.currentLineIndex, 1);

    // on réinitialise les index
    this.subtitle.lines.forEach((line, index) => {
      line.index = index;
      line.words.forEach((w) => (w.indexLine = index));
    });

    if (this.currentLineIndex === 0) {
      this.currentLineIndex--;
    }
  },

  // ***************************************************************************************
  // calculateTimeWords est une fonction permettant de calculer la durée de tous les mots de la ligne
  // ***************************************************************************************
  calculateTimeWords(line, startTime, endTime) {
    const sentence = this.getSentence(line);

    let inTime = startTime;
    const outTime = endTime;

    line.words.forEach((word) => {
      const duration = outTime - inTime;

      const time = (duration / sentence.length) * word.text.length;
      const inOutTime = parseFloat((inTime + time).toFixed(2));

      word.in = inTime;
      word.out = inOutTime;

      inTime = inOutTime;
    });

    last(line.words).out = outTime;
  },

  /*getNextWord() {
    const nextWord = this.currentLine.words[this.currentWordIndex + 1];
    if (nextWord) {
      return nextWord;
    }

    const nextLine = this.subtitle.lines[this.currentLineIndex + 1];
    if (nextLine && nextLine.words.length) {
      return first(nextLine.words);
    }

    return null;
  },*/

  // ***************************************************************************************
  // pasteWords permet de coller les mots dans la liste
  // ***************************************************************************************
  pasteWords(pasteTexts, fromClipboard) {
    if (this.underSelection) {
      //this.onDeleteSelection('onPaste');

      if (this.lettersToSelect.length) {
        const nbWords = groupBy(
          this.lettersToSelect,
          (x) => x.indexWord && x.indexLine
        );
        const keys = Object.keys(nbWords);

        // Si c'est un seul mot
        if (keys.length === 1) {
          this.underSelection = false;

          const startLetter = first(this.lettersToSelect);
          const endLetter = last(this.lettersToSelect);

          const word =
            this.subtitle.lines[startLetter.indexLine].words[
              startLetter.indexWord
            ];

          this.currentWordIndex = word.index;
          this.currentLineIndex = word.indexLine;
          this.currentLetterIndex = endLetter.indexLetter;

          // on historise
          this.addNewActionForUndo('onPasteLetter');

          const newText = fromClipboard
            ? join(pasteTexts, '')
            : join(
                pasteTexts.map((x) => x.text),
                ''
              );

          word.text =
            word.text.substring(0, startLetter.index) +
            newText +
            word.text.substring(endLetter.index + 1);

          this.onChange();
        }
      }

      return;
    }

    const { fontColor, fontItalic } = this.props.subtitleParameters;
    // Faire l'historique
    this.addNewActionForUndo('onPaste');

    // s'il y a qu'un mot à coller dans le mot
    if (pasteTexts.length === 1) {
      const pasteText = fromClipboard
        ? join(pasteTexts, ' ')
        : join(
            pasteTexts.map((x) => x.text),
            ' '
          );

      if (this.currentWord) {
        // on colle le mot là où le curseur est placé
        const currentText = this.currentWord.text;
        const currentIndex = this.currentWord.letters.findIndex(
          (l) => l.indexLetter === this.currentLetterIndex
        );

        let newText;
        if (currentText === ' ') {
          newText = pasteText;
        } else {
          newText =
            currentText.substring(0, currentIndex) +
            pasteText +
            currentText.substring(currentIndex);
        }

        this.currentWord.text = newText;

        this.currentLetterIndex = this.currentLetterIndex + pasteText.length;
      } else {
        const words = this.getWords();

        const newWord = {
          color: fontColor,
          italic: fontItalic,
          text: pasteText,
          in: this.subtitle.in,
          out: words.length ? last(words).in : this.subtitle.out,
        };

        this.currentLine.words.push(newWord);

        //  in: this.subtitle.in,
        //   out: outTime,
      }

      this.onChange();
      return;
    }

    let oldWord = undefined;

    const newWords = [];

    if (this.currentWord) {
      oldWord = { ...this.currentWord };
    }

    // si le texte est vide
    if (oldWord && (oldWord.text.length === 0 || oldWord.text === ' ')) {
      // si le mot est un mot vide, on le supprime
      this.currentLine.words.splice(oldWord.index, 1);
      //this.currentWordIndex--;

      const lastWord = this.currentLine.words[oldWord.index];
      if (lastWord) {
        oldWord = { ...lastWord };
      }
    }

    if (!oldWord) {
      const words = this.getWords();

      oldWord = {
        index: 0,
        color: fontColor,
        italic: fontItalic,
        text: '',
        in: this.subtitle.in,
        out: words.length ? last(words).in : this.subtitle.out,
      };
    }

    pasteTexts.forEach((element) => {
      let prevWord = newWords.length ? last(newWords) : oldWord;

      const inTime = prevWord ? prevWord.out : oldWord.in;

      let newWord = {
        in: inTime,
        text: fromClipboard ? element : element.text,
        color: fromClipboard ? fontColor : element.color,
        italic: fromClipboard ? fontItalic : element.color,
      };

      newWord.out = calculateTime(
        inTime,
        oldWord.out,
        oldWord.text,
        newWord.text
      );

      newWords.push(newWord);
    });

    // si oldWord n'est pas le dernier de la phrase
    if (
      this.currentLine.words.length &&
      this.currentWordIndex < last(this.currentLine.words).index
    ) {
      const wordsToChange = this.currentLine.words.filter(
        (x) => x.index > this.currentWordIndex
      );

      newWords.push(...wordsToChange);

      this.currentLine.words = this.currentLine.words.filter(
        (x) => x.index <= this.currentWordIndex
      );
    }

    // si le dernier mot pushé est un mot vide, on le supprime
    if (last(newWords).text === '') {
      newWords.pop();
    }

    let currentLine = this.currentLine;
    let indexNewLine = -1;

    for (let index = 0; index < newWords.length; index++) {
      const word = newWords[index];

      const sentence = this.getSentence(currentLine);

      // Si on dépasse alors on va à la ligne
      const maxLength = this.getMaxLength(currentLine);
      if (sentence.length + word.text.length > maxLength) {
        let nextLine = this.subtitle.lines[currentLine.index + 1];

        if (!nextLine) {
          nextLine = this.createNewLine(this.subtitle, this.currentLine);
          if (!nextLine) {
            // on décale les mots sur le sous-titre précédent
            const prevLine = this.subtitle.lines[currentLine.index - 1];
            if (prevLine) {
              currentLine.words.push(word);

              const maxLengthPrevLine = this.getMaxLength(prevLine);
              const firstWord = first(currentLine.words);

              const success = this.shiftWordsToPrev(
                this.subtitle,
                maxLengthPrevLine,
                prevLine,
                currentLine,
                firstWord
              );

              if (success) {
                continue;
              }

              // on revert
              prevLine.words.shift();
            }

            break;
          }

          nextLine.index = currentLine.index + 1;
          this.subtitle.lines.push(nextLine);
        }

        nextLine.words.splice(0, 0, word);

        indexNewLine = 1;
        currentLine = nextLine;

        this.currentLineIndex++;
        this.currentWordIndex = 0;
      } else {
        if (indexNewLine > -1) {
          currentLine.words.splice(indexNewLine, 0, word);
          indexNewLine++;
        } else {
          currentLine.words.push(word);
        }

        this.currentWordIndex++;
      }
    }

    const words = this.getWords();
    // si le dernier mot pushé est un mot vide, on le supprime
    if (last(words)?.text === '') {
      newWords.pop();
      last(words).out = oldWord.out;
    }

    this.onChange();
  },

  // ***************************************************************************************
  // removeEmptyWord Si on a créé un mot vide et qu'on fait une action ailleurs, on doit le supprimer
  // L'action est appelé quand on fait une action par le clavier (shortCut) ou par la souris (mouseDown)
  // ***************************************************************************************
  async removeEmptyWord(action) {
    if (!this.hasEmptyWord) {
      return;
    }

    const actionsNotProceed = ['BACKSPACE', 'ENTER', 'DELETE', 'PASTE'];

    // Si une de ces actions est en cours d'exécution, on ne fait rien
    // car on supprimera déjà le mot vide
    if (actionsNotProceed.includes(action)) {
      this.hasEmptyWord = false;
      return;
    }

    const spaceWord = this.getWords().find((x) => x.text === '');

    if (!spaceWord) {
      this.hasEmptyWord = false;
      return;
    }

    // on historise
    this.addNewActionForUndo('addSpace');

    const currentLine = this.subtitle.lines[spaceWord.indexLine];
    currentLine.words.splice(spaceWord.index, 1);

    this.recalculateIndexes();

    if (action === 'RIGHT') {
      this.currentWordIndex--;
      this.currentLetterIndex--;
    }

    this.hasEmptyWord = false;
    this.onChange();
  },

  recalculateIndexes() {
    this.subtitle.lines.forEach((line) => {
      line.words.forEach((word, index) => {
        word.index = index;
      });
    });
  },

  // ***************************************************************************************
  // groupWordByChar permet de regrouper deux mots par un caractère
  // ***************************************************************************************
  groupWordByChar(action, charCode, next) {
    const { index, text } = this.currentWord;
    const { words } = this.currentLine;

    this.addNewActionForUndo(action);

    if (next) {
      const nextWord = words[index + 1];

      if (!nextWord || (charCode && nextWord.text.includes(charCode))) {
        this.cancelUndo();
        return;
      }

      this.currentWord.text += charCode + nextWord.text;
      this.currentWord.in = nextWord.in;

      this.currentLine.words.splice(nextWord.index, 1);
    } else {
      const prevWord = words[index - 1];
      if (!prevWord || prevWord.text.includes(charCode)) {
        this.cancelUndo();
        return;
      }

      prevWord.text += charCode + text;
      prevWord.in = this.currentWord.in;

      this.currentLine.words.splice(index, 1);

      this.currentWordIndex = prevWord.index;
    }

    this.onChange();
  },

  // ***************************************************************************************
  // createWords permet de créer une liste de mots
  // ***************************************************************************************
  createWords(texts) {
    const { text, in: inTime, out: outTime } = this.currentWord;

    const newWords = [];

    let inOutTime = inTime;

    texts.forEach((newText, index) => {
      const newWord = {
        ...this.currentWord,
        text: newText,
        in: inOutTime,
        letters: [],
      };

      inOutTime = calculateTime(inOutTime, outTime, text, newText);
      newWord.out = inOutTime;

      if (index === texts.length - 1) {
        newWord.out = outTime;
      }

      newWords.push(newWord);
    });

    return newWords;
  },

  // ***************************************************************************************
  // setPositionOfSearch() Cette fonction permet de positionner le curseur sur le mot lors de la recherche du mot
  // ***************************************************************************************
  async setPositionOfSearch(currentPosition, wordsToSearch) {
    const words = this.getWords();
    const wordToSearch = wordsToSearch[currentPosition];

    if (wordToSearch) {
      const word = first(wordToSearch.words);

      const wordToSelect = words.find(
        (x) => 
          x.indexWord === word.indexWord
          && x.text === word.text 
          //&& x.in === word.inWord
      );

      if (wordToSelect) {
        /*if (!this.state.active) {
          this.setState({ active: true });
        }*/

        if (wordToSelect.indexLine === undefined) {
          this.forceRedraw = true;
          await wait(100);
        }

        this.currentLineIndex = wordToSelect.indexLine;
        this.currentWordIndex = wordToSelect.index;
        this.onRefreshCanvas();
      }
    }
  },

  // ***************************************************************************************
  // createNewLine permet de créer une nouvelle ligne
  // ***************************************************************************************
  createNewLine(currentSubtitle, currentLine, toUp = true) {
    const {
      verticalPos,
      doubleHeight,
      displayNumberOfLines,
      defaultAlignment,
      numberOfLines,
      teletext,
    } = this.props.subtitleParameters;


    

    // On est limité au nombre de lignes prédéfinies
    if (currentSubtitle.lines.length === numberOfLines) {
      if (currentSubtitle.lines.every((x) => x.words.length)) {
        return null;
      }

      currentSubtitle.lines.forEach((line, index) => {
        if (!line.words.length) {
          currentSubtitle.lines.splice(index, 1);
        }
      });
    }

    if (!toUp) {
      const vPos = currentLine.verticalPos ?? verticalPos;
      let prevPos = doubleHeight ? 2 : 1;

      // On descend le sous-titre
      if (vPos - prevPos <= 0) {
        this.onMoveLineToDown(currentLine);
        prevPos = 0;
      }

      // on créé une nouvelle ligne
      const newLine = {
        len: 0,
        verticalPos: currentLine.verticalPos - prevPos,
        horizontalPos: currentLine.horizontalPos ?? 7.5,
        doubleHeight: currentLine.doubleHeight ?? true,
        align: currentLine.align ?? defaultAlignment,
        words: [],
        speakerId: currentLine.speakerId,
      };

      return newLine;
    }

    let vPos = currentLine.verticalPos ?? verticalPos;
    const nextPos = doubleHeight ? 2 : 1;

    // On ne peut pas créer de lignes au delà du maximum prédéfinie,
    // du coup, on monte les lignes avant de créer une nouvelle ligne
    if (vPos + nextPos >= displayNumberOfLines) {
      if (!currentLine.verticalPos) {
        currentLine.verticalPos = vPos;
      }

      this.onMoveLineToUp(currentLine);

      
      if (teletext &&  doubleHeight) {
        //this.onMoveLineToUp(currentLine);
      }
    } else {
      vPos += nextPos;
    }

    // on créé une nouvelle ligne
    const newLine = {
      len: 0,
      verticalPos: vPos,
      horizontalPos: currentLine.horizontalPos ?? 7.5,
      doubleHeight: currentLine.doubleHeight ?? true,
      align: currentLine.align ?? defaultAlignment,
      words: [],
      speakerId: currentLine.speakerId,
    };

    return newLine;
  },

  // ***************************************************************************************
  // createNewWord permet de créer un nouveau mot
  // ***************************************************************************************
  createNewWord(currentSubtitle, char, indexLine = 0) {

    const { fontColor, fontItalic } = this.props.subtitleParameters;
    
    const currentLine = currentSubtitle.lines[indexLine];

    const words = this.getWords();

    currentLine.words.push({
      color: fontColor,
      italic: fontItalic,
      text: char,
      in: currentSubtitle.in,
      out: words.length ? last(words).in : this.subtitle.out,
    });
  },

  // ***************************************************************************************
  // shiftWordsToNext permet de décaler les sous-titres sur la ligne suivante
  // ***************************************************************************************
  shiftWordsToNext(currentSubtitle, maxLength, firstLine, lastLine, word) {
    let success = true;

    for (let i = 0; i < currentSubtitle.lines.length - 1; i++) {
      let currentLine = currentSubtitle.lines[i];
      let nextLine = currentSubtitle.lines[i + 1];

      let maxLengthCurrent = this.getMaxLength(currentLine);
      let maxLengthNext = this.getMaxLength(nextLine);

      // Tant que la ligne courante dépasse le maxLength
      while (this.getSentence(currentLine).length > maxLengthCurrent) {
        if (!currentLine.words.length) break;

        const wordToMove = last(currentLine.words);
        const nbLetters = wordToMove.letters
          ? wordToMove.letters.length
          : wordToMove.text.length;

        // Vérifier si la ligne suivante dépasse son maxLength
        const nbCharsCurrent = this.getSentence(currentLine).length - nbLetters;
        const nbCharsNext = this.getSentence(nextLine).length + nbLetters;

        if (nbCharsCurrent > maxLengthCurrent || nbCharsNext > maxLengthNext) {
          success = false;
          break;
        }

        // On déplace le dernier mot de la ligne courante vers le début de la suivante
        currentLine.words.pop();
        nextLine.words.unshift(wordToMove);
      }
    }

    if (success) {
      if (currentSubtitle.lines.length === 1) {
        success = false;
      }
    }

    if (!success) {
      const { numberOfLines } = this.props.subtitleParameters;
      const totalMaxLength = maxLength * numberOfLines;

      // On créé une nouvelle ligne si on n'a pas atteint le nombre de lignes autorisé
      if (
        currentSubtitle.lines.length <= numberOfLines - 1 &&
        currentSubtitle.len <= totalMaxLength
      ) {
        firstLine.words.shift();
        const newLine = this.createNewLine(
          currentSubtitle,
          first(currentSubtitle.lines),
          false
        );

        if (newLine) {
          newLine.words.push(word);
          currentSubtitle.lines.unshift(newLine);
        } else {
          return false;
        }
      } else {
        // on annule tout
        lastLine.words.push(firstLine.words.shift());
        return false;
      }
    }

    return true;
  },

  // ***************************************************************************************
  // shiftWordsToPrev permet de décaler les sous-titres sur la ligne précédente
  // ***************************************************************************************
  shiftWordsToPrev(currentSubtitle, maxLength, firstLine, lastLine, word) {
    let success = true;

    for (let i = currentSubtitle.lines.length - 2; i >= 0; i--) {
      let nextLine = currentSubtitle.lines[i + 1];
      let currentLine = currentSubtitle.lines[i];

      let maxLengthCurrent = this.getMaxLength(currentLine);
      let maxLengthNext = this.getMaxLength(nextLine);

      // Tant que la ligne suivante dépasse le maxLength
      while (this.getSentence(nextLine).length > maxLengthNext) {
        if (!nextLine.words.length) break;

        const wordToMove = first(nextLine.words);
        const nbLetters = wordToMove.letters
          ? wordToMove.letters.length
          : wordToMove.text.length;

        // Vérifier si la ligne courante dépasse son maxLength
        const nbCharsCurrent = this.getSentence(currentLine).length + nbLetters;

        if (nbCharsCurrent > maxLengthCurrent) {
          success = false;
          break;
        }

        // On déplace le premier mot de la ligne suivante vers la fin de la courante
        nextLine.words.shift();
        currentLine.words.push(wordToMove);
      }
    }

    if (success) {
      if (currentSubtitle.lines.length === 1) {
        success = false;
      }
    }

    if (!success) {
      const { numberOfLines } = this.props.subtitleParameters;
      const totalMaxLength = maxLength * numberOfLines;

      // On créé une nouvelle ligne si on n'a pas atteint le nombre de lignes autorisé
      if (
        currentSubtitle.lines.length <= numberOfLines - 1 &&
        this.subtitle.len <= totalMaxLength
      ) {
        lastLine.words.pop();

        const newLine = this.createNewLine(
          currentSubtitle,
          last(currentSubtitle.lines)
        );
        if (newLine) {
          newLine.words.push(word);
          currentSubtitle.lines.push(newLine);
        } else {
          return false;
        }
      } else {
        // on annule tout
        firstLine.words.unshift(lastLine.words.pop());

        return false;
      }
    }

    return true;
  },

  clearSelection() {
    if (this.underSelection) {
      this.underSelection = false;
      this.wordsToSelect = [];
      this.lettersToSelect = [];
    }
  },
};
